import { useEffect, useState } from "react";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./Calendar.styles.scss";

// Package Imports
import {
  Calendar as BigCalendar,
  luxonLocalizer,
  Formats,
} from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import { v4 as uuidv4 } from "uuid";
import { DateTime, Settings } from "luxon";

// Type Imports
import {
  IIntervals,
  IIntervalsExtended,
  IStaticOverrideSchedule,
} from "@pulse-ui/types/components/AGTable";
import dayjs from "dayjs";

// Hook Imports
import { useAlert } from "@/utils/hooks/useAlert/useAlert";
import { IStaticPriceOverrideUploadModalStatus } from "@/pages/StaticPriceOverrides/StaticPriceOverrides.types";

type CalendarEvent = {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resource: { original: IIntervalsExtended };
};

// Configure Luxon properly for UTC
Settings.defaultZone = "utc"; // Set default timezone to UTC
Settings.defaultLocale = "en"; // Keep locale as English

const localizer = luxonLocalizer(DateTime, {
  firstDayOfWeek: 0,
});

const formats: Partial<Formats> = {
  dateFormat: "dd",
  dayFormat: "EEEE",
  weekdayFormat: "cccc",
  // Use explicit UTC formatting for times
  timeGutterFormat: (date: Date) => {
    return DateTime.fromJSDate(date, { zone: "utc" }).toFormat("HH:mm");
  },
  eventTimeRangeFormat: ({ start, end }: { start: Date; end: Date }) =>
    DateTime.fromJSDate(start, { zone: "utc" }).toFormat("HH:mm") +
    " – " +
    DateTime.fromJSDate(end, { zone: "utc" }).toFormat("HH:mm") +
    " (UTC)",
  agendaTimeFormat: (date: Date) => {
    return DateTime.fromJSDate(date, { zone: "utc" }).toFormat("HH:mm");
  },
};

const weekdayMap: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

const DnDCalendar = withDragAndDrop(BigCalendar);

const Calendar = ({
  schedule,
  setStaticPriceOverrideUploadModalStatus,
  setIntervalsMap,
}: {
  schedule: IStaticOverrideSchedule | null;
  setStaticPriceOverrideUploadModalStatus: React.Dispatch<
    React.SetStateAction<IStaticPriceOverrideUploadModalStatus>
  >;
  setIntervalsMap: React.Dispatch<
    React.SetStateAction<Map<string, IIntervalsExtended>>
  >;
}) => {
  // State - START -------------------------------------
  const [timeSlots, setTimeSlots] = useState<CalendarEvent[]>([]);
  const { pushAlert } = useAlert();
  // State - END ---------------------------------------

  // Methods - START -----------------------------------
  const mapIntervals = () => {
    if (!schedule) return;
    const temp_map = new Map();

    for (let key of Object.keys(
      schedule
    ) as (keyof IStaticOverrideSchedule)[]) {
      if (key === "symbol") continue;

      const intervals = schedule[key] as IIntervalsExtended[];

      intervals.forEach((interval: IIntervals) => {
        const { start_time, end_time, price } = interval;
        const new_uuid = uuidv4();

        const new_interval: IIntervalsExtended = {
          uuid: new_uuid,
          start_time,
          end_time,
          price,
          weekday: key.toUpperCase(),
          symbol: schedule.symbol,
        };

        temp_map.set(new_uuid, new_interval);
      });
    }

    parseIntervalsMapToEvents(temp_map);
    setIntervalsMap(temp_map);
  };

  const parseIntervalsMapToEvents = (
    intervals_map: Map<string, IIntervalsExtended>
  ) => {
    const mappedEvents: CalendarEvent[] = [];

    intervals_map.forEach((interval, uuid) => {
      const { start_time, end_time, price, weekday } = interval;

      const [sh, sm] = start_time.split(":").map(Number);
      const [eh, em] = end_time.split(":").map(Number);

      const baseDate = DateTime.utc(2023, 1, 1);
      const dayOffset = weekdayMap[weekday.toLowerCase()];

      if (dayOffset === undefined) {
        pushAlert({
          id: uuidv4(),
          message: `Invalid weekday: ${weekday}`,
          status: 500,
        });
        return;
      }

      const startDate = baseDate
        .plus({ days: dayOffset })
        .set({ hour: sh, minute: sm });
      const endDate = baseDate
        .plus({ days: dayOffset })
        .set({ hour: eh, minute: em });

      mappedEvents.push({
        id: uuid,
        title: `$${parseFloat(price)}`,
        start: startDate.toJSDate(),
        end: endDate.toJSDate(),
        resource: { original: interval },
      });
    });

    setTimeSlots(mappedEvents);
  };

  const getWeekdayFromDayjsDay = (day: number): string => {
    const weekday = Object.keys(weekdayMap).find(
      (key) => weekdayMap[key] === day
    );
    return weekday ? weekday.toUpperCase() : "";
  };

  const handleSelectSlot = async ({
    start,
    end,
    action,
  }: {
    start: Date;
    end: Date;
    slots: Date[];
    action: string;
  }) => {
    if (action !== "click") {
      if (start && end) {
        const dayjs_start = dayjs.utc(start);
        const dayjs_end = dayjs.utc(end);
        // Get the day of the week from the weekdayMap using the day_from_dayjs
        const day_from_dayjs = getWeekdayFromDayjsDay(dayjs_start.get("day"));
        setStaticPriceOverrideUploadModalStatus((prev: any) => {
          return {
            ...prev,
            open: true,
            id: uuidv4(),
            start_time: dayjs_start,
            end_time: dayjs_end,
            day: day_from_dayjs?.toUpperCase() || null,
            symbol: schedule?.symbol || null,
            type: "update",
          };
        });
      } else {
        pushAlert({
          id: uuidv4(),
          message: "Invalid date format",
          status: 500,
        });
      }
    }
  };

  const handleSelectEvent = (e: any) => {
    const resource = e.resource.original;
    const normalizedStartTime = resource.start_time.padStart(8, "0");
    const normalizedEndTime = resource.end_time.padStart(8, "0");

    setStaticPriceOverrideUploadModalStatus((prev: any) => {
      return {
        ...prev,
        open: true,
        id: e.id,
        symbol: resource.symbol || null,
        start_time: dayjs.utc(`2023-01-01T${normalizedStartTime}Z`),
        end_time: dayjs.utc(`2023-01-01T${normalizedEndTime}Z`),
        day: resource.weekday.toUpperCase(),
        price: resource.price,
        type: "update",
      };
    });
  };

  const handleEventResize = (e: any) => {
    const { start, end } = e;
    const resource = e.event.resource.original;

    setStaticPriceOverrideUploadModalStatus((prev: any) => {
      return {
        ...prev,
        open: true,
        id: e.event.id,
        symbol: resource.symbol || null,
        start_time: dayjs.utc(start),
        end_time: dayjs.utc(end),
        day: resource.weekday.toUpperCase(),
        price: resource.price,
        type: "update",
      };
    });
  };

  const handleEventDrop = (e: any) => {
    const { start, end } = e;
    const resource = e.event.resource.original;
    const new_weekday = getWeekdayFromDayjsDay(dayjs.utc(start).get("day"));

    setStaticPriceOverrideUploadModalStatus((prev: any) => {
      return {
        ...prev,
        open: true,
        id: e.event.id,
        start_time: dayjs.utc(start),
        end_time: dayjs.utc(end),
        day: new_weekday,
        symbol: resource.symbol || null,
        price: resource.price,
        type: "update",
      };
    });
  };
  // Methods - END -------------------------------------

  // useEffects - START --------------------------------
  useEffect(() => {
    if (schedule) {
      mapIntervals();
    } else {
      setTimeSlots([]);
    }
  }, [schedule]);
  // useEffects - END ----------------------------------

  return (
    <DnDCalendar
      localizer={localizer}
      defaultView="week"
      views={["week"]}
      defaultDate={DateTime.utc(2023, 1, 10).toJSDate()}
      min={DateTime.utc(2023, 1, 1, 0, 0).toJSDate()}
      max={DateTime.utc(2023, 1, 1, 23, 59).toJSDate()}
      style={{ height: "85vh" }}
      events={timeSlots}
      formats={formats}
      getNow={() => DateTime.utc().toJSDate()}
      components={{
        toolbar: () => null,
      }}
      onSelectSlot={handleSelectSlot}
      onDoubleClickEvent={(event: any) => handleSelectEvent(event)}
      onEventDrop={(event: any) => handleEventDrop(event)}
      onEventResize={(event: any) => handleEventResize(event)}
      // Explicitly tell calendar to use UTC for dates
      startAccessor={(event: any) => event.start}
      endAccessor={(event: any) => event.end}
      // Enable selection
      selectable
      // Set default timeslot duration
      step={30} // 30 minutes
      timeslots={2} // 2 slots per step
      allDayAccessor={(e: any) => {
        return (
          e.resource.original.start_time === "00:00:00" &&
          e.resource.original.end_time === "00:00:00"
        );
      }}
    />
  );
};

export default Calendar;
